import React from "react";
import { Row, Col } from "react-bootstrap";
import { formatFloat, formatPercentage, formatUSD } from "../../utils/format";
import DetailsRow from "../DetailsRow";
import RiskMetrics from "../RiskMetrics";
import { useOnchainAccountBorrowable, useOnchainAccountLendingPool } from "../../hooks/useOnchain";
import useGenericCall from "../../hooks/useGenericCall";
import { getWeightedAPR } from "../../utils";
import { PoolTokenType } from "impermax-sdk";


/**
 * Generates lending pool aggregate details.
 */
export default function AccountLendingPoolDetailsLeverage() {
  const data = useGenericCall(useOnchainAccountLendingPool(), async onchainAccountLendingPool => {
    const currentLeverage = await onchainAccountLendingPool.getLeverage();
    const offchainLendingPool = await onchainAccountLendingPool.getLendingPool().getOffchainLendingPool()
    return {
      LPEquityUSD: await onchainAccountLendingPool.getLPEquityUSD(),
      LPEquity: await onchainAccountLendingPool.getLPEquity(),
      collateralUSD: await onchainAccountLendingPool.getCollateral().getDepositedUSD(),
      debtUSD: await onchainAccountLendingPool.getDebtUSD(),
      currentLeverage,
      totalAPR: await offchainLendingPool.getTotalAPR() * currentLeverage,
      borrowedUSD: [
        await onchainAccountLendingPool.getBorrowableA().getBorrowedUSD(), 
        await onchainAccountLendingPool.getBorrowableB().getBorrowedUSD()
      ]
    }
  });
  const bData = [PoolTokenType.BorrowableA, PoolTokenType.BorrowableB].map(
    borrowable => useGenericCall(useOnchainAccountBorrowable(borrowable), async onchainAccountBorrowable => ({
      borrowedUSD: await onchainAccountBorrowable.getBorrowedUSD(),
      borrowAPR: await (await onchainAccountBorrowable.getPoolToken().getOffchainBorrowable()).getBorrowAPR(),
      farmingAPR: await (await onchainAccountBorrowable.getPoolToken().getOffchainBorrowable()).getFarmingAPR(),
    }))
  );

  if (!data || !bData[0] || !bData[1]) return (null);

  const borrowAPR = getWeightedAPR(data.currentLeverage, bData[0].borrowedUSD, bData[0].borrowAPR, bData[1].borrowedUSD, bData[1].borrowAPR);
  const farmingAPR = getWeightedAPR(data.currentLeverage, bData[0].borrowedUSD, bData[0].farmingAPR, bData[1].borrowedUSD, bData[1].farmingAPR);
  
  const leveragedAPR = data.totalAPR + farmingAPR - borrowAPR;

  const LPEquityExplanation = "Calculated as: Total Collateral - Total Debt";

  return (<>
    <Row className="account-lending-pool-details">
      <Col sm={12} md={6}>
        <DetailsRow name={"Total Collateral"} value={formatUSD(data.collateralUSD)} />
        <DetailsRow name={"Total Debt"} value={formatUSD(data.debtUSD)} />
        <DetailsRow name={"LP Equity"} value={`${formatUSD(data.LPEquityUSD)} (${formatFloat(data.LPEquity)} LP)`} explanation={LPEquityExplanation} />
        <DetailsRow name={"Estimated APR"} value={formatPercentage(leveragedAPR)} />
      </Col>
      <Col sm={12} md={6}>
        <RiskMetrics />
      </Col>
    </Row>
  </>);
}